import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../lib/auth/auth-context';
import { loginSchema, type LoginInput } from '../lib/auth/schemas';
import { friendlyAuthError } from '../lib/auth/errors';
import { safeRedirectPath } from '../lib/auth/guards';
import { AuthShell, ErrorAlert } from '../components/auth/AuthShell';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (input) => loginSearchSchema.parse(input),
});

function LoginPage(): JSX.Element {
  const { signInWithPassword, isAuthenticated, isReady } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const target = safeRedirectPath(search.redirect);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Already-signed-in users hitting /login (e.g., via back button) are
  // pushed onto whichever page they were trying to reach.
  useEffect(() => {
    if (isReady && isAuthenticated) {
      void navigate({ to: target });
    }
  }, [isReady, isAuthenticated, target, navigate]);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const { error } = await signInWithPassword(values.email, values.password);
    if (error) {
      setSubmitError(friendlyAuthError('login', error));
      return;
    }
    void navigate({ to: target });
  });

  return (
    <AuthShell
      title="Sign in"
      subtitle="Pharmacy portal access"
      footer={
        <span>
          <Link to="/forgot-password" className="text-primary hover:underline">
            Forgot password?
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {submitError ? <ErrorAlert>{submitError}</ErrorAlert> : null}
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          error={errors.email?.message}
          {...register('email')}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <Button type="submit" loading={isSubmitting} fullWidth>
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
